import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase/config';
import type { GenerationRecipe } from '../features/canvas/types/recipe';
import { createOperationId } from '../utils/operationId';

export type GenerationModel = 'krea2' | 'ltx25';

export interface GenerateImageRequest {
  operationId?: string;
  model: GenerationModel;
  prompt: string;
  width: number;
  height: number;
  seed?: number;
  loraStrength?: number;
  characterId?: string;
  characterIds?: string[];
  projectId?: string;
  recipe?: GenerationRecipe;
}

export interface GenerateImageResult {
  requestId: string;
  image: string;
  imageStoragePath: string;
  // Plus de vignette fichier pour les images (US R2-10) — null au retour.
  thumbnailStoragePath: string | null;
  imageUrl: string | null;
  seed: number;
  executionTime: number;
  delayTime: number;
}

export type VideoAspectRatio =
  | '9:16 (Portrait Widescreen)'
  | '16:9 (Landscape Widescreen)';

export const VIDEO_ASPECT_RATIOS: { value: VideoAspectRatio; label: string }[] = [
  { value: '9:16 (Portrait Widescreen)', label: '9:16' },
  { value: '16:9 (Landscape Widescreen)', label: '16:9' },
];

export const VIDEO_DURATION_MIN = 1;
export const VIDEO_DURATION_MAX = 10;
export const VIDEO_DURATION_DEFAULT = 5;
export const VIDEO_DURATION_OPTIONS = [3, 5, 10];

export interface GenerateVideoRequest {
  operationId?: string;
  model: 'ltx25';
  prompt: string;
  duration: number;
  aspectRatio: VideoAspectRatio;
  audio?: boolean;
  hd?: boolean;
  sourceImagePath?: string;
  seed?: number;
  projectId?: string;
  recipe?: GenerationRecipe;
}

export interface GenerateVideoResult {
  requestId: string;
  mediaType: 'video';
  imageStoragePath: string;
  thumbnailStoragePath?: string | null;
  imageUrl: string | null;
  seed: number;
  executionTime: number;
  delayTime: number;
}

export interface EndpointHealth {
  jobs: { inQueue?: number; inProgress?: number; completed?: number; failed?: number };
  workers: {
    idle?: number;
    initializing?: number;
    running?: number;
    throttled?: number;
    unhealthy?: number;
  };
}

export interface EndpointHealthAggregate {
  models: Record<GenerationModel, EndpointHealth>;
}

type GenerationStatus =
  | 'IN_QUEUE'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'TIMED_OUT'
  | 'CANCELLED';

interface GenerationSubmission {
  requestId: string;
  seed: number;
  status: GenerationStatus;
}

interface GenerationStatusResult {
  requestId: string;
  status: GenerationStatus;
  seed: number;
  mediaType?: 'video';
  image?: string;
  imageStoragePath?: string;
  thumbnailStoragePath?: string | null;
  imageUrl?: string | null;
  executionTime?: number;
  delayTime?: number;
  error?: string;
  errorCode?: string;
  failureKind?: string;
}

interface CallableError extends Error {
  code?: string;
  details?: unknown;
}

const SUBMIT_TIMEOUT_MS = 120_000;
const STATUS_TIMEOUT_MS = 180_000;
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_INTERVAL_MS = 10_000;
const MISSING_JOB_GRACE_MS = 60_000;
// Deadline GLOBALE du suivi : au-delà de la deadline totale RunPod
// (TOTAL_DEADLINE_MS = 20 min côté serveur) + marge de réconciliation. Sans
// elle, une erreur « transitoire » répétée (ex. lecture Storage du média en
// échec côté getGenerationStatus) faisait tourner la boucle de polling à
// l'infini — orbe éternelle dans le rail alors que le job était terminé.
const AWAIT_TIMEOUT_MS = 25 * 60 * 1000;

// Options de suivi — hors du payload callable (jamais sérialisées vers le
// serveur) : isAborted permet à l'appelant (store de la file) d'interrompre
// le polling d'un run soldé par ailleurs (entrée promptHistory arrivée via
// le listener — cf. adoptHistoryEntries).
export interface GenerationFollowOptions {
  isAborted?: () => boolean;
}

const submitImageFn = httpsCallable<GenerateImageRequest, GenerationSubmission>(
  functions,
  'generateImage',
  { timeout: SUBMIT_TIMEOUT_MS }
);
const submitVideoFn = httpsCallable<GenerateVideoRequest, GenerationSubmission>(
  functions,
  'generateImage',
  { timeout: SUBMIT_TIMEOUT_MS }
);
const getGenerationStatusFn = httpsCallable<{ jobId: string }, GenerationStatusResult>(
  functions,
  'getGenerationStatus',
  { timeout: STATUS_TIMEOUT_MS }
);
const getEndpointHealthFn = httpsCallable<
  { model?: GenerationModel },
  EndpointHealth | EndpointHealthAggregate
>(functions, 'getEndpointHealth');

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const errorCode = (error: unknown) => (error as CallableError | null)?.code ?? '';
const errorReason = (error: unknown) => {
  const details = (error as CallableError | null)?.details;
  return (details as { reason?: string } | undefined)?.reason;
};
const isTerminalSubmissionError = (error: unknown) => {
  const code = errorCode(error);
  return [
    'invalid-argument',
    'resource-exhausted',
    'permission-denied',
    'unauthenticated',
    'failed-precondition',
    'already-exists',
    'not-found',
  ].some((suffix) => code.endsWith(suffix));
};
const isTransientStatusError = (error: unknown) => {
  const code = errorCode(error);
  return (
    !code ||
    code.endsWith('internal') ||
    code.endsWith('unavailable') ||
    code.endsWith('deadline-exceeded') ||
    code.endsWith('unknown')
  );
};
const terminalGenerationError = (result: GenerationStatusResult): CallableError => {
  const error = new Error(result.error || 'Image generation failed, please retry') as CallableError;
  error.code = `functions/${result.errorCode || 'internal'}`;
  if (result.failureKind) error.details = { reason: result.failureKind };
  return error;
};

const awaitGeneration = async (
  operationId: string,
  submissionError?: unknown,
  isAborted?: () => boolean
): Promise<GenerationStatusResult> => {
  let delay = POLL_INTERVAL_MS;
  let missingSince: number | null = null;
  const startedAt = Date.now();
  for (;;) {
    // Run soldé par un autre chemin (adoption via l'historique) : on arrête
    // de poller. L'erreur est interceptée par l'appelant (scope.aborted).
    if (isAborted?.()) throw new Error('Generation follow aborted');
    // Filet de sécurité : jamais de boucle sans fin — au-delà de la deadline
    // le run bascule en échec visible au lieu d'une orbe éternelle.
    if (Date.now() - startedAt >= AWAIT_TIMEOUT_MS) {
      const timeoutError = new Error(
        'Generation status could not be confirmed, please retry'
      ) as CallableError;
      timeoutError.code = 'functions/deadline-exceeded';
      throw timeoutError;
    }
    let result: GenerationStatusResult | undefined;
    let statusError: unknown;
    try {
      const response = await getGenerationStatusFn({ jobId: operationId });
      result = response.data;
    } catch (error) {
      statusError = error;
    }

    if (result) {
      missingSince = null;
      delay = POLL_INTERVAL_MS;
      if (result.status === 'COMPLETED' || result.status === 'FAILED') return result;
    } else {
      const code = errorCode(statusError);
      const isMissing = code.endsWith('not-found');
      if (isMissing) {
        if (!submissionError) throw statusError;
        missingSince = missingSince ?? Date.now();
        if (Date.now() - missingSince >= MISSING_JOB_GRACE_MS) throw submissionError;
      } else if (!isTransientStatusError(statusError)) {
        throw statusError;
      }
      delay = Math.min(MAX_POLL_INTERVAL_MS, delay * 2);
    }
    await sleep(delay);
  }
};

const submitAndAwait = async (
  params: GenerateImageRequest | GenerateVideoRequest,
  options?: GenerationFollowOptions
): Promise<GenerationStatusResult> => {
  const operationId = params.operationId ?? createOperationId();
  const request = { ...params, operationId };
  let submissionError: unknown;
  try {
    if (params.model === 'ltx25') {
      await submitVideoFn(request as GenerateVideoRequest);
    } else {
      await submitImageFn(request as GenerateImageRequest);
    }
  } catch (error) {
    if (isTerminalSubmissionError(error) && errorReason(error) !== 'submission-unknown') throw error;
    submissionError = error;
  }
  return awaitGeneration(operationId, submissionError, options?.isAborted);
};

export const requestImageGeneration = async (
  params: GenerateImageRequest,
  options?: GenerationFollowOptions
): Promise<GenerateImageResult> => {
  const result = await submitAndAwait(params, options);
  if (result.status === 'FAILED') throw terminalGenerationError(result);
  // US R2-10 : plus de vignette fichier pour les images — seul l'original est
  // exigé (la vignette 256 est une rendition à la volée du Worker).
  if (!result.imageStoragePath) {
    throw new Error('Generation completed without its persisted media');
  }
  return {
    requestId: result.requestId,
    image: result.image ?? '',
    imageStoragePath: result.imageStoragePath,
    thumbnailStoragePath: result.thumbnailStoragePath ?? null,
    imageUrl: result.imageUrl ?? null,
    seed: result.seed,
    executionTime: result.executionTime ?? 0,
    delayTime: result.delayTime ?? 0,
  };
};

export const requestVideoGeneration = async (
  params: GenerateVideoRequest,
  options?: GenerationFollowOptions
): Promise<GenerateVideoResult> => {
  const result = await submitAndAwait(params, options);
  if (result.status === 'FAILED') throw terminalGenerationError(result);
  if (!result.imageStoragePath) throw new Error('Generation completed without its persisted media');
  return {
    requestId: result.requestId,
    mediaType: 'video',
    imageStoragePath: result.imageStoragePath,
    thumbnailStoragePath: result.thumbnailStoragePath,
    imageUrl: result.imageUrl ?? null,
    seed: result.seed,
    executionTime: result.executionTime ?? 0,
    delayTime: result.delayTime ?? 0,
  };
};

export interface MediaDeletionResult {
  status: 'pending' | 'completed';
  taskIds: string[];
  queuedFiles: number;
}

export interface DeleteProjectMediaResult extends MediaDeletionResult {
  deletedDocs: number;
}

const deleteProjectMediaFn = httpsCallable<
  { projectId: string },
  DeleteProjectMediaResult
>(functions, 'deleteProjectMedia');

export const deleteProjectMedia = async (
  projectId: string
): Promise<DeleteProjectMediaResult> => {
  const result = await deleteProjectMediaFn({ projectId });
  return result.data;
};

const deleteMediaEntryFn = httpsCallable<
  { entryId: string },
  MediaDeletionResult
>(functions, 'deleteMediaEntry');

export const deleteMediaEntry = async (entryId: string): Promise<MediaDeletionResult> => {
  const result = await deleteMediaEntryFn({ entryId });
  return result.data;
};

export function fetchEndpointHealth(model: GenerationModel): Promise<EndpointHealth>;
export function fetchEndpointHealth(): Promise<EndpointHealthAggregate>;
export async function fetchEndpointHealth(
  model?: GenerationModel
): Promise<EndpointHealth | EndpointHealthAggregate> {
  const result = await getEndpointHealthFn(model ? { model } : {});
  return result.data;
}
